import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { ArrowLeft } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

/**
 * Shared shell for the 5 operational workspaces. Handles auth gate, header,
 * and a consistent tab layout for sub-modules.
 */
export function WorkspaceShell({ title, subtitle, tabs, defaultTab, headerActions, dataTestId }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const allowed = !!user && user.role === "admin";

  useEffect(() => {
    if (!loading && !allowed) nav("/login");
  }, [loading, allowed, nav]);

  if (loading || !allowed) {
    return <Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>;
  }

  const showTabBar = tabs.length > 1;

  return (
    <Layout>
      <section className="border-b border-[hsl(var(--line))] bg-[#0A0A0A] text-white">
        <div className="container-summit py-10 md:py-12">
          <Link to="/admin" className="text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white inline-flex items-center gap-2" data-testid="ws-back">
            <ArrowLeft size={12} /> All Workspaces
          </Link>
          <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="overline text-white/60"><span className="accent-rule" />Event Operations</div>
              <h1 className="serif text-4xl sm:text-5xl mt-3" data-testid={dataTestId}>{title}</h1>
              {subtitle && <p className="mt-3 max-w-2xl text-white/70 text-sm">{subtitle}</p>}
            </div>
            {headerActions && <div className="flex flex-wrap gap-2">{headerActions}</div>}
          </div>
        </div>
      </section>

      <section className="container-summit py-8">
        <Tabs defaultValue={defaultTab || tabs[0]?.value} className="w-full">
          {showTabBar && (
            <TabsList className="rounded-none border-b border-zinc-200 justify-start w-full overflow-x-auto flex bg-transparent p-0 h-auto">
              {tabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value}
                  className="rounded-none uppercase tracking-[0.18em] text-xs data-[state=active]:border-b-2 data-[state=active]:border-black py-3 px-4"
                  data-testid={`ws-tab-${t.value}`}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          )}
          {tabs.map((t) => (
            <TabsContent key={t.value} value={t.value} className={showTabBar ? "pt-6" : "pt-0"}>
              {t.render()}
            </TabsContent>
          ))}
        </Tabs>
      </section>
    </Layout>
  );
}
